import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "@/i18n/IntlProvider";
import { CodeDetailPanel } from "@/components/codes/CodeDetailPanel";

/**
 * /$locale/codes/$id — full detail page for a single QR code.
 *
 * Header + back link live in the route; the loaded panel handles the
 * sidebar slot (identity + lifecycle CTAs) and the main editing surface.
 * See web/src/app/[locale]/(app)/codes/[id]/page.tsx for the legacy
 * Next.js shape — same layout, but the BFF flow means no more
 * setRequestLocale dance and no oidc-spa bootstrap race.
 */
export function CodesDetailRoute() {
  const { locale, id } = useParams({ strict: false }) as {
    locale?: string;
    id?: string;
  };
  const lang = locale ?? "de";
  const codeId = id ?? "";
  const t = useTranslations("app.codes.detail");

  return (
    // No max-width wrapper here — the editor needs the full main width so
    // QrGenerator's `lg:grid-cols-[1fr_1.6fr_1fr]` has room to lay out
    // Inhalt | QR | Gestaltung side-by-side. The legacy layout boxed
    // /codes/$id at max-w-screen-xl, which on wide displays (2500 px+)
    // letterboxes the editor with empty bands left and right; matching
    // the Marketing surface (no max-width on its `<main>`) keeps the
    // experience consistent across the two app shells.
    <section className="space-y-6">
      <header>
        <Link
          to={"/$locale/codes" as never}
          params={{ locale: lang } as never}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {t("back")}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {t("title")}
        </h1>
      </header>
      <CodeDetailPanel id={codeId} />
    </section>
  );
}
